/**
 * MonthEmployeeSection Component
 *
 * Renders a single employee's accordion section in the month view.
 * Contains the header with employee badge and stats, and the content
 * with week rows.
 */

import { Accordion } from "@/components/ui/display/Accordion";
import { UserBadge } from "@/components/ui/display/UserBadge";
import { AggregateRow } from "@/components/schedule/Schedule/AggregateRow";
import { EditModeButton } from "@/components/shared/EditModeButton";
import { formatEmployeeName } from "@/hooks/data/dataQuality";
import classNames from "classnames";
import React from "react";
import styles from "../Schedule.module.css";
import type { MonthEmployeeSectionProps } from "../types";
import { MonthWeekRow } from "./MonthWeekRow";

export const MonthEmployeeSection: React.FC<MonthEmployeeSectionProps> =
  React.memo(
    ({
      employee,
      stats,
      weekGroups,
      daysPerWeek,
      hideWeekend,
      hoursInDay,
      hideAggregate,
      selectMode,
      selectedCardId,
      selectionCount,
      isSelected,
      isInRangePreview,
      setRangeHoverTarget,
      editingEmployeeId,
      showIndividualEditButtons,
      getBookingsForDay,
      getCardForDay,
      getWeeklySum,
      handleCardClick,
      onWeekLabelClick,
      onCardClick,
      onEnterEmployeeEditMode,
      onExitEmployeeEditMode,
    }) => {
      // Check if this employee's cells are interactive
      const isInteractive =
        editingEmployeeId === null || editingEmployeeId === employee.id;

      return (
        <Accordion.Item
          key={employee.id}
          value={String(employee.id)}
          className={styles.monthEmployeeSection}
          data-testid={`month-employee-section-${employee.id}`}
          data-edit-mode={editingEmployeeId === employee.id}
        >
          {/* Employee Header Row */}
          <Accordion.Trigger
            chevronVariant="minimal"
            chevronClassName={classNames(
              styles.collapseButton,
              styles.monthCollapseButton,
            )}
            className={styles.monthEmployeeHeader}
            data-testid={`month-collapse-toggle-${employee.id}`}
          >
            {/* Employee Name Badge */}
            <div className={styles.monthEmployeeChip}>
              <UserBadge
                name={formatEmployeeName(employee)}
                size="sm"
                mode="auto"
                data-testid={`month-employee-badge-${employee.id}`}
              />
            </div>

            {/* Spacer absorbs space between chip and aggregate */}
            <div className={styles.monthEmployeeSpacer} />

            {/* Aggregate Row - Progress stats (hidden for soft bookings/customer mode) */}
            {!hideAggregate && (
              <div className={styles.monthEmployeeStats}>
                <AggregateRow
                  bookedHours={stats.totalHours}
                  possibleHours={stats.totalCapacity}
                  data-testid={`month-aggregate-${employee.id}`}
                />
              </div>
            )}
          </Accordion.Trigger>

          {/* Week Grids */}
          <Accordion.Content className={styles.monthContentWrapper}>
            <div className={styles.monthWeeksContainer}>
              {weekGroups.map((week) => (
                <MonthWeekRow
                  key={week.weekNumber}
                  week={week}
                  employee={employee}
                  daysPerWeek={daysPerWeek}
                  hideWeekend={hideWeekend}
                  hoursInDay={hoursInDay}
                  hideAggregate={hideAggregate}
                  selectMode={selectMode}
                  selectedCardId={selectedCardId}
                  selectionCount={selectionCount}
                  isSelected={isSelected}
                  isInRangePreview={isInRangePreview}
                  setRangeHoverTarget={setRangeHoverTarget}
                  isInteractive={isInteractive}
                  getBookingsForDay={getBookingsForDay}
                  getCardForDay={getCardForDay}
                  getWeeklySum={getWeeklySum}
                  handleCardClick={handleCardClick}
                  onWeekLabelClick={onWeekLabelClick}
                  onCardClick={onCardClick}
                />
              ))}
            </div>

            {/* Individual Edit Button (when multiple employees selected) */}
            {showIndividualEditButtons && (
              <div className={styles.employeeEditButtonContainer}>
                <EditModeButton
                  enabled={editingEmployeeId === employee.id}
                  disabled={
                    editingEmployeeId !== null &&
                    editingEmployeeId !== employee.id
                  }
                  onClick={() =>
                    editingEmployeeId === employee.id
                      ? onExitEmployeeEditMode?.()
                      : onEnterEmployeeEditMode?.(employee.id)
                  }
                  data-testid={
                    editingEmployeeId === employee.id
                      ? `employee-edit-mode-cancel-btn-${employee.id}`
                      : `employee-edit-mode-btn-${employee.id}`
                  }
                />
              </div>
            )}
          </Accordion.Content>
        </Accordion.Item>
      );
    },
  );

MonthEmployeeSection.displayName = "MonthEmployeeSection";

export default MonthEmployeeSection;
